/* eslint-disable  */
import React, { useEffect } from 'react';
import { Box, TextField, Button, Grid, Card, CardContent, CardHeader, FormControlLabel, Checkbox } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { useBranchCode } from 'hooks/api/terminology/branch-code/useBranchCode';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { globalColor } from 'views/ConstantColors';

// Validation Schema
const BranchCodeSchema = Yup.object({
    name: Yup.string().required('Branch name is required'),
    code: Yup.string().required('Branch code is required'),
    description: Yup.string().required('Description is required'),
    isActive: Yup.boolean(),
    effectiveFrom: Yup.date().nullable(),
    effectiveTo: Yup.date().nullable().min(Yup.ref('effectiveFrom'), 'Effective To date must be later than Effective From date')
});

export const BranchCodeForm = ({ initialData, onClear }) => {
    const isEditMode = Boolean(initialData?.id);
    const { createBranchCode, updateBranchCode } = useBranchCode();
    useEffect(() => {}, [initialData, isEditMode]);
    const initialValues = {
        id: initialData?.id || 0, // Add default ID for new records
        name: initialData?.name || '',
        code: initialData?.code || '',
        description: initialData?.description || '',
        isActive: initialData?.isActive ?? true,
        effectiveFrom: initialData?.effectiveFrom || null,
        effectiveTo: initialData?.effectiveTo || null,
        isDeleted: false,
        createdBy: initialData?.createdBy || '',
        lastModifiedBy: initialData?.lastModifiedBy || '',
        createdAt: initialData?.createdAt || new Date().toISOString(),
        lastModifiedAt: initialData?.lastModifiedAt || new Date().toISOString()
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const formattedData = {
                id: values.id,
                name: values.name,
                code: values.code,
                description: values.description,
                isActive: values.isActive,
                isDeleted: values.isDeleted,
                effectiveFrom: values.effectiveFrom ? new Date(values.effectiveFrom).toISOString() : null,
                effectiveTo: values.effectiveTo ? new Date(values.effectiveTo).toISOString() : null,
                createdBy: values.createdBy,
                lastModifiedBy: values.lastModifiedBy,
                createdAt: values.createdAt,
                lastModifiedAt: new Date().toISOString()
            };


            if (isEditMode) {
                await updateBranchCode(initialData.id, formattedData);
                Swal.fire('Success', 'Branch Code updated successfully!', 'success');
            } else {
                await createBranchCode(formattedData);
                Swal.fire('Success', 'Branch Code added successfully!', 'success');
                resetForm();
            }

            if (onClear) {
                onClear();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire('Error', error.message || 'There was an error processing your request.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Rest of the component remains the same...
    // JSX structure with all the form fields
    return (
        <Card elevation={3} sx={{ mb: 4 }}>
            <CardHeader
                title={isEditMode ? 'Edit Branch Code' : 'Add New Branch Code'}
                sx={{
                    backgroundColor: globalColor,
                    color: 'white',
                    '.MuiCardHeader-title': { color: 'white', fontSize: '1.2rem', fontWeight: 'bold' }
                }}
            />
            <CardContent>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Formik initialValues={initialValues} validationSchema={BranchCodeSchema} onSubmit={handleSubmit} enableReinitialize>
                        {({ errors, touched, values, setFieldValue, resetForm }) => (
                            <Form>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            name="name"
                                            label="Branch Name"
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            name="code"
                                            label="Branch Code"
                                            error={touched.code && Boolean(errors.code)}
                                            helperText={touched.code && errors.code}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            name="description"
                                            label="Description"
                                            multiline
                                            rows={3}
                                            error={touched.description && Boolean(errors.description)}
                                            helperText={touched.description && errors.description}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            label="Effective From"
                                            value={values.effectiveFrom}
                                            onChange={(date) => setFieldValue('effectiveFrom', date)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    error={touched.effectiveFrom && Boolean(errors.effectiveFrom)}
                                                    helperText={touched.effectiveFrom && errors.effectiveFrom}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            label="Effective To"
                                            value={values.effectiveTo}
                                            onChange={(date) => setFieldValue('effectiveTo', date)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    error={touched.effectiveTo && Boolean(errors.effectiveTo)}
                                                    helperText={touched.effectiveTo && errors.effectiveTo}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.isActive}
                                                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                                                    name="isActive"
                                                />
                                            }
                                            label="Active"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                onClick={() => {
                                                    resetForm();
                                                    if (onClear) onClear();
                                                }}
                                                sx={{
                                                    bgcolor: globalColor,
                                                    '&:hover': {
                                                        bgcolor: 'rgb(0, 100, 0)'
                                                    }
                                                }}
                                            >
                                                {isEditMode ? 'Cancel' : 'Reset'}
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                sx={{
                                                    bgcolor: globalColor,
                                                    '&:hover': {
                                                        bgcolor: 'rgb(0, 100, 0)'
                                                    }
                                                }}
                                            >
                                                {isEditMode ? 'Update Branch Code' : 'Add Branch Code'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </LocalizationProvider>
            </CardContent>
        </Card>
    );
};
